import { parsePressure, parseLength } from '../../utils/units.js'

class FootingConditionsDialogComponent extends HTMLElement {

  footingType = 'Spread'
  soilBearing = 2500 // psf
  concreteStrength = 3 // ksi
  tensileStrength = 60 // ksi
  concreteDensity = 150 // psf
  cover = 3 // inches
  
  constructor() {
    super()
    document.addEventListener('project-loaded', async (event) => this.onProjectLoaded(event.detail), true)
    this.addEventListener('show.bs.modal', this.configure)
  }

  /**
   * Create our view model and configure the view
   * @param {FootingDesign} model
   * 
   */
  onProjectLoaded(model) {
    this.footingType = model.footingConditions.footingType
    this.soilBearing = model.footingConditions.soilBearing
    this.concreteStrength = model.footingConditions.concreteStrength
    this.tensileStrength = model.footingConditions.tensileStrength
    this.concreteDensity = model.footingConditions.concreteDensity
    this.cover = model.footingConditions.cover
  }

  publishUpdate() {
    const event = new CustomEvent('footing-conditions-dialog-update', { detail: this });
    this.dispatchEvent(event)
  }

  onSoilBearingBlur() {
    const value = parsePressure(document.getElementById("soil-bearing-input").value)
    if(isNaN(value) || value <= 0) {
      document.getElementById('soil-bearing-validation').classList.remove('d-none')
      return
    }
    document.getElementById('soil-bearing-validation').classList.add('d-none') 
    this.soilBearing = value
    this.publishUpdate()
  }

  onConcreteStrengthBlur() {
    const value = parsePressure(document.getElementById("concrete-strength-input").value)
    if(isNaN(value) || value <= 0) {
      document.getElementById('concrete-strength-validation').classList.remove('d-none')
      return
    }
    document.getElementById('concrete-strength-validation').classList.add('d-none') 
    this.concreteStrength = value
    this.publishUpdate()
  }

  onTensileStrengthBlur() {
    const value = parsePressure(document.getElementById("tensile-strength-input").value)
    if(isNaN(value) || value <= 0) {
      document.getElementById('tensile-strength-validation').classList.remove('d-none')
      return
    }
    document.getElementById('tensile-strength-validation').classList.add('d-none') 
    this.tensileStrength = value
    this.publishUpdate()
  }

  onConcreteDensityBlur() {
    const value = parseInt(document.getElementById("concrete-density-input").value)
    if(isNaN(value) || value <= 0) {
      document.getElementById('concrete-density-validation').classList.remove('d-none')
      return
    }
    document.getElementById('concrete-density-validation').classList.add('d-none') 
    this.concreteDensity = value
    this.publishUpdate()
  }

  onRebarCoverBlur() {
    const value = parseLength(document.getElementById("rebar-cover-input").value)
    if(isNaN(value) || value <= 0) {
      document.getElementById('rebar-cover-validation').classList.remove('d-none')
      return
    }
    document.getElementById('rebar-cover-validation').classList.add('d-none') 
    this.cover = value
    this.publishUpdate()
  }

  configure() {
    document.getElementById("soil-bearing-input").value = this.soilBearing
    document.getElementById("concrete-strength-input").value = this.concreteStrength
    document.getElementById("tensile-strength-input").value = this.tensileStrength
    document.getElementById("concrete-density-input").value = this.concreteDensity
    document.getElementById("rebar-cover-input").value = this.cover
  }

  connectedCallback() {
    this.innerHTML = String.raw`
<div class="modal fade" id="modal-footing-conditions-dialog" tabindex="-1" role="dialog" 
aria-labelledby="modal-service-conditions-dialog" aria-hidden="true">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Footing Conditions</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <h4 class="text-center d-none">Select Footing Type</h4>
        <div class="modal-body modal-body-shadow text-center d-none">
          <button class="btn btn-primary button-shadow" id='column-footing-button'>Column Footing</button>
          <button class="btn btn-default button-shadow" id='wall-footing-button'>Wall Footing</button>
        </div>
        <div style="margin:10px 0px 10px 0px;">
          <table class="table">
            <tr>
              <td>Soil Bearing (<em>q<sub>allowable</sub></em>)</td>
              <td>
                <input style="width:100px;" id="soil-bearing-input" type="text" name="soil-bearing"
                  class="text-center" value="${this.soilBearing}" /> psf
                <div  style="width:200px;" id="soil-bearing-validation" class="alert alert-danger d-none">
                  Please enter a valid tensile strength. e.g. 2500 or 2500 psf
                </div>
              </td>
            </tr>
            <tr>
              <td>Concrete Strength (<em>f'<sub>c</sub></em>)</td>
              <td>
                <input style="width:100px;" id="concrete-strength-input" type="text" name="concrete-strength"
                  class="text-center" value="${this.concreteStrength}" /> ksi
                <div  style="width:200px;" id="concrete-strength-validation" class="alert alert-danger d-none">
                  Please enter a valid concrete strength. e.g. 3 or 3 ksi
                </div>
              </td>
            </tr>
            <tr>
              <td>Steel Tensile Yield Strength (<em>f<sub>y</sub></em>)</td>
              <td>
                <input style="width:100px;" id="tensile-strength-input" type="text" name="tensile-strength"
                  class="text-center" value="${this.tensileStrength}" /> ksi
                <div  style="width:200px;" id="tensile-strength-validation" class="alert alert-danger d-none">
                  Please enter a valid tensile yield strength. e.g. 60 or 60 ksi
                </div>
              </td>
            </tr>

            <tr>
              <td>Concrete Density (<em>&gamma;<sub>c</sub></em>)</td>
              <td>
                <input style="width:100px;" id="concrete-density-input" type="text" name="concrete-density"
                  class="text-center" value="${this.concreteDensity}" /> lb/ft<sup>3</sup>
                <div  style="width:200px;" id="concrete-density-validation" class="alert alert-danger d-none">
                  Please enter a valid concrete density. e.g. 150
                </div>
              </td>
            </tr>

            <tr>
              <td>Reinforcement Bar Cover</td>
              <td>
                <input style="width:100px;" id="rebar-cover-input" type="text" name="rebar-cover"
                  class="text-center" value="${this.cover}" /> inches
                <div  style="width:200px;" id="rebar-cover-validation" class="alert alert-danger d-none">
                  Please enter a valid rebar cover. e.g. 3 or 3 in
                </div>
              </td>
            </tr>

          </table>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-default" data-bs-dismiss="modal">Close</button>
      </div>
    </div>
  </div>
</div>`

    document.getElementById('soil-bearing-input').onblur = (() => this.onSoilBearingBlur())
    document.getElementById('concrete-strength-input').onblur = (() => this.onConcreteStrengthBlur())
    document.getElementById('tensile-strength-input').onblur = (() => this.onTensileStrengthBlur())
    document.getElementById('concrete-density-input').onblur = (() => this.onConcreteDensityBlur())
    document.getElementById('rebar-cover-input').onblur = (() => this.onRebarCoverBlur())

    this.configure()
  }
}

customElements.define('footing-conditions-dialog-component', FootingConditionsDialogComponent)

